import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, SimpleChanges } from "@angular/core";
import { Chart } from "chart.js";
import { DataStream } from "src/app/shared/models/kpi.model";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { CommonModule } from "@angular/common";

@Component({
  selector: "gen-kpi-bar",
  templateUrl: "./gen-kpi-bar.component.html",
  styleUrls: ["./gen-kpi-bar.component.scss"],
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GenKpiBarComponent implements OnInit, OnDestroy {
  @Input() height: string;
  @Input() width: string;
  @Input() id: string = "";
  @Input() data: DataStream[];
  @Input() customTooltipId: string;
  @Input() displayLegend: boolean = true;

  canvas: any;
  ctx: any;
  myChart: any;
  chartId: string;

  tooltipYLabel: string;
  tooltipDataPoints: [] = [];

  regularPct: DataStream;
  criticalPct: DataStream;
  overduePct: DataStream;
  total: DataStream;
  onlineCnt: DataStream;
  phoneCnt: DataStream;

  constructor(private cdr: ChangeDetectorRef) { }

  ngOnDestroy() {
    if (this.myChart) this.myChart.destroy();
  }

  ngOnInit() {
    this.chartId = "chart_" + this.id;

    if (this.id.startsWith("plant_orders")) {
      this.regularPct = this.data.find((d) => d.streamCode == "REGULAR_PCT");
      this.criticalPct = this.data.find((d) => d.streamCode == "CRITICAL_PCT");
      this.overduePct = this.data.find((d) => d.streamCode == "OVERDUE_PCT");
      this.total = this.data.find((d) => d.streamCode == "TOTAL");
    } else if (this.id.startsWith("supply_orders")) {
      this.onlineCnt = this.data.find((d) => d.streamCode == "ONLINE_CNT");
      this.phoneCnt = this.data.find((d) => d.streamCode == "PHONE_CNT");
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.drawChart();
  }

  ngAfterViewInit() {
    this.drawChart();
  }

  getDataSets(): Array<any> {
    let datasets = [];
    this.data
      .filter((d) => d.streamCode != "LABELS" && d.visible)
      .forEach((s) => {
        let dataset = {
          label: s.descr,
          data: this.getDataFromStream(s),
          fill: true,
          backgroundColor: s.color,
          code: s.streamCode,
          txtValues: s.txtValues,
          barThickness: this.id.startsWith("plant_orders")
            ? 50
            : this.id.startsWith("cycle_duration")
              ? 40
              : 80,
          borderRadius: {
            topLeft: 4,
            topRight: 4,
            bottomLeft: 0,
            bottomRight: 0,
          },
        };
        datasets.push(dataset);
      });
    return datasets;
  }

  getDataFromStream(stream: DataStream): any[] {
    let data = [];
    for (let i = 0; i < stream.labels.length; i++) {
      data.push({
        x: stream.labels[i],
        y: stream.values[i],
      });
    }
    return data;
  }

  drawChart() {
    if (this.myChart) this.myChart.destroy();

    this.canvas = document.getElementById(this.chartId);
    if (!this.canvas) return;

    this.ctx = this.canvas.getContext("2d");
    Chart.defaults.font = {
      family: "Rubik",
      size: 12,
    };
    this.myChart = new Chart(this.ctx, {
      type: "bar",
      data: {
        labels: this.data.filter((d) => d.streamCode == "LABELS")[0].labels,
        datasets: this.getDataSets(),
      },
      plugins: [ChartDataLabels],
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: {
          duration: 0,
        },
        layout: {
          padding: {
            top: 20,
          },
        },
        scales: {
          x: {
            stacked: true,
            display: !this.id.startsWith("supply_orders"),
          },
          y: {
            stacked: true,
            display: false,
          },
        },
        plugins: {
          title: {
            display: this.id.startsWith("supply_orders"),
            text: "הזמנות איסוף",
            font: {
              size: 14,
              // weight: "400",
            },
          },
          datalabels: {
            labels: {
              value: {
                align: this.id.startsWith("plant_orders") ? "end" : "center",
                anchor: this.id.startsWith("plant_orders") ? "end" : "center",
                offset: 5,
                color: this.id.startsWith("plant_orders") ? "black" : "white",
                font: { size: 12, weight: "bold" },
                formatter: (value, ctx) => {
                  if (this.id.startsWith("plant_orders")) {
                    if (ctx.datasetIndex < 2) return "";
                    else return this.total.values[ctx.dataIndex] + " עבודות";
                  } else if (this.id.startsWith("supply_orders")) {
                    let str = value.y.toString().includes(".")
                      ? value.y
                        .toString()
                        .substring(0, value.y.toString().indexOf("."))
                      : value.y.toString();
                    return str + "%";
                  } else {
                    return "";
                  }
                },
              },
              metadata: this.id.startsWith("supply_orders")
                ? {
                  align: "bottom",
                  color: "white",
                  font: { size: 12, weight: 400 },

                  formatter: (value, ctx) => {
                    let datastream: DataStream;
                    switch (ctx.dataset["code"]) {
                      case "ONLINE_PCT":
                        datastream = this.onlineCnt;
                        break;
                      case "PHONE_PCT":
                        datastream = this.phoneCnt;
                        break;
                    }
                    return (
                      datastream.values[0].toString() + " " + datastream.descr
                    );
                  },
                }
                : null,
            },
          },
          legend: {
            onClick: null,
            display: this.displayLegend,
            position: "bottom",
            align: "center",
            rtl: true,
            labels: {
              usePointStyle: true,
              boxWidth: 8,
              boxHeight: 8,
              font: {
                size: 12,
              },
              // boxPadding: 10,
              padding: 10,
            },
          },
          tooltip: {
            enabled:
              !this.customTooltipId && !this.id.startsWith("supply_orders"),
            external: this.customTooltipId ? this.externalTooltipHandler : null,
            position: "nearest",
            usePointStyle: true,
            displayColors: false,
            backgroundColor: "rgb(235, 238, 242)",
            bodyColor: "rgb(0, 148, 221)",
            titleColor: "rgb(0, 148, 221)",
            rtl: false,
            padding: 6,
            callbacks: {
              label: (tooltipItem) => {
                return (
                  (tooltipItem.dataset.label || "").replace("בתהליך", "") +
                  ": " +
                  tooltipItem.formattedValue
                );
              },
              title: (tooltipItems) => {
                if (tooltipItems.length == 0) {
                  return "";
                }
                if (!this.id.startsWith("plant_orders")) {
                  return tooltipItems[0].label;
                }
                let datastream: DataStream;
                switch (tooltipItems[0].dataset["code"]) {
                  case "REGULAR":
                    datastream = this.regularPct;
                    break;
                  case "CRITICAL":
                    datastream = this.criticalPct;
                    break;
                  case "OVERDUE":
                    datastream = this.overduePct;
                    break;
                }

                let pct = datastream.values[tooltipItems[0].dataIndex] * 100;
                let str = pct.toString().includes(".")
                  ? pct.toString().substring(0, pct.toString().indexOf("."))
                  : pct.toString();
                return str + "%";
              },
            },
          },
        },
      },
    });
  }

  getOrCreateTooltip = (chart) => {
    let tooltipEl = chart.canvas.parentNode.querySelector("div");

    if (!tooltipEl) {
      tooltipEl = document.getElementById(this.customTooltipId);
      tooltipEl.style.opacity = 1;
      tooltipEl.style.pointerEvents = "none";
      tooltipEl.style.position = "absolute";
      tooltipEl.style.transform = "translate(-50%, 0)";
      tooltipEl.style.transition = "all .1s ease";
      chart.canvas.parentNode.appendChild(tooltipEl);
    }

    return tooltipEl;
  };

  externalTooltipHandler = (context) => {
    const { chart, tooltip } = context;
    const tooltipEl = this.getOrCreateTooltip(chart);

    if (tooltip.opacity === 0) {
      tooltipEl.style.opacity = 0;
      return;
    }

    this.tooltipYLabel = tooltip.title[0];
    this.tooltipDataPoints = tooltip.dataPoints;

    const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;

    tooltipEl.style.opacity = 1;
    tooltipEl.style.left = positionX + tooltip.caretX + "px";
    tooltipEl.style.top = positionY + tooltip.caretY + 15 + "px";
    tooltipEl.style.font = tooltip.options.bodyFont.string;
    tooltipEl.style.padding =
      tooltip.options.padding + "px " + tooltip.options.padding + "px";
    this.cdr.markForCheck();
  };
}
